import type {
  AssemblyCurrency,
  FlowPostResponse,
  MemberDetails,
  ReactionDetails,
  RepliesResponse,
} from '@assembly-web/services';

import { HorizontalRule } from '../../../DesignSystem/Layout/HorizontalRule';
import type { AnnouncementBannerProps } from './AnnouncementBanner';
import { AnnouncementBanner } from './AnnouncementBanner';
import { PostCardBody } from './PostCardBody';
import { PostCardFooter } from './PostCardFooter';
import { PostCardHeader } from './PostCardHeader';

export enum PostCardActions {
  PersonClicked = 'person',
  FlowNameClicked = 'flowName',
  RepliesBarClicked = 'replies',
  TaskClicked = 'task',
}

type AnnouncementResponse = {
  color: string;
  title: string;
  viewsCount: number;
  description: string;
  showInsights: boolean;
  announcementId: string;
};

export type PostCardProps = {
  canShowAnnouncements: boolean;
  repliesData: RepliesResponse;
  flowResponse: FlowPostResponse;
  reactions: ReactionDetails[];
  announcementData?: AnnouncementResponse;
  onReactionClick: (emoji: ReactionDetails) => void;
  onReplyButtonClick: () => void;
  currentMember: MemberDetails;
  onPostCardHeaderClick: (action: PostCardActions, id: string) => void;
  onPostCardBodyClick: (action: PostCardActions, id: string) => void;
  currency: AssemblyCurrency;
  onFileClick: (responseBlock: { blockId: string; fileName: string }) => void;
  onToolbarItemClick?: (responseBlock: {
    blockId: string;
    fileName: string;
    itemId: string;
  }) => void;
  openAnnouncementsInsightsModal?: (announcementId: string) => void;
  onAnnouncementDescriptionHover?: (args: AnnouncementBannerProps) => void;
};

export function PostCard(props: PostCardProps) {
  const {
    repliesData,
    flowResponse,
    reactions,
    currentMember,
    onReactionClick,
    onReplyButtonClick,
    onPostCardHeaderClick,
    onPostCardBodyClick,
    currency,
    onFileClick,
    onToolbarItemClick,
    openAnnouncementsInsightsModal,
    canShowAnnouncements,
    onAnnouncementDescriptionHover,
  } = props;

  const {
    respondent,
    createdAt,
    flow: {
      name: flowName,
      icon: { value: flowIcon },
      flowId,
    },
    visibility,
    responses,
  } = flowResponse;

  const isRecognition = flowId === 'recognition';

  return (
    <div className="rounded-lg border border-gray-5 bg-gray-1">
      {canShowAnnouncements && (
        <>
          {flowResponse.activeAnnouncement?.color && (
            <AnnouncementBanner
              color={flowResponse.activeAnnouncement.color}
              title={flowResponse.activeAnnouncement.title}
              description={flowResponse.activeAnnouncement.description}
              onAnnouncementDescriptionHover={onAnnouncementDescriptionHover}
            />
          )}
        </>
      )}
      <div className="px-4 pb-2 pt-4">
        <PostCardHeader
          respondent={respondent}
          createdAt={createdAt}
          flowName={flowName}
          visibility={visibility}
          flowIcon={flowIcon}
          flowId={flowId}
          onPostCardHeaderClick={onPostCardHeaderClick}
          isEdited={isRecognition && responses[0].edited} // only show for recognition
          {...(canShowAnnouncements &&
            flowResponse.activeAnnouncement?.showInsights && {
              openViewInsightsModal: () =>
                openAnnouncementsInsightsModal?.(
                  flowResponse.activeAnnouncement?.announcementId ?? ''
                ),
            })}
        />
        <PostCardBody
          onPostCardBodyClick={onPostCardBodyClick}
          flowResponse={flowResponse}
          currency={currency}
          onFileClick={onFileClick}
          onToolbarItemClick={onToolbarItemClick}
        />
        <HorizontalRule />
        <PostCardFooter
          reactions={reactions}
          repliesData={repliesData}
          currentMember={currentMember}
          onReactionClick={onReactionClick}
          onReplyButtonClick={onReplyButtonClick}
          hideReplies={flowResponse.hideReplies ?? false}
          hideReactions={flowResponse.hideReactions ?? false}
        />
      </div>
    </div>
  );
}
